import { TFMSetupInspiredList } from 'api/contentful/futureMessage'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { SDot, SDotsContainer } from 'components/dots/DotsContainer'
import { FutureMessageCardByUuid } from 'components/futureMessage/futureMessageCard/FutureMessageCard'
import {
  SFutureMessageInspiration,
  SFutureMessageInspirationHeader,
  SIconButton,
} from './FutureMessageInspiration.styled'
import { HorizontalScroll } from 'components/horizontalScroll/HorizontalScroll'
import { Icon } from 'components/icon'
import { Spacer } from 'components/spacer/Spacer'
import { BlockSpinner } from 'components/spinner/Spinner'
import { HStack } from 'components/Stack'
import { Text, Title, Title_h3_default } from 'components/Typography'
import debounce from 'lodash.debounce'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { pxToRem } from 'utils/styled'

const CONST_CARD_SIZE = 300
const CONST_GAP_SIZE = 24

type TListItem = {
  uuid: string
  isActive: boolean
}

export const FutureMessageInspiration = (props: {
  className?: string
  inspiredList?: TFMSetupInspiredList
  onAction?: () => void
  more?: boolean
}) => {
  const { t } = useTranslation('future_message')

  const [, setRender] = useState(0)
  const [list, setList] = useState<TListItem[]>()
  useEffect(() => {
    setList(
      props.inspiredList?.map?.((uuid, index) => {
        return {
          uuid: uuid ?? `Item_${index}`,
          isActive: false,
        }
      })
    )
  }, [props.inspiredList])

  const carouselRef = useRef<HTMLDivElement>(null)
  const scroll = (amount: number) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: amount, behavior: 'smooth' })
    }
  }
  const scrollTo = (uuid: string | null, index: number) => {
    const elem = document.getElementById(`Future_${index}_${uuid}`)
    const elemBoundBox = elem?.getBoundingClientRect?.()
    if (elemBoundBox) {
      scroll(elemBoundBox.left - elemBoundBox.width / 2)
    }
  }

  const checkCardsInView = useMemo(() => {
    return debounce(
      () => {
        const baseWidth = carouselRef.current?.clientWidth
        if (baseWidth) {
          list?.forEach((listItem, index) => {
            const elem = document.getElementById(`Future_${index}_${listItem.uuid}`)
            const elemBoundBox = elem?.getBoundingClientRect?.()

            if (elemBoundBox && baseWidth) {
              const centerXPos = elemBoundBox.left + elemBoundBox.width / 2
              listItem.isActive = centerXPos > 0 && centerXPos < baseWidth
            }
          })

          setRender((value) => value + 1) // force render
        }
      },
      50,
      { maxWait: 300 }
    )
  }, [list])

  useEffect(() => {
    const carousel = carouselRef.current
    if (carousel) {
      checkCardsInView()
      carousel.addEventListener('scroll', checkCardsInView)
    }
    return () => {
      if (carousel) carousel.removeEventListener('scroll', checkCardsInView)
    }
  }, [checkCardsInView])

  const renderNavigation = (showIcons: boolean) => {
    return (
      <>
        <SDotsContainer>
          {list?.map((item, i) => (
            <SDot key={i} isActive={item.isActive} onClick={() => scrollTo(item.uuid, i)} />
          ))}
        </SDotsContainer>

        {showIcons && (
          <>
            <SIconButton
              css={{ transform: 'rotateY(180deg)' }}
              onClick={() => scroll(-(CONST_CARD_SIZE + CONST_GAP_SIZE))}
            >
              <Icon name="ChevronRight" size="1.25rem" />
            </SIconButton>

            <SIconButton onClick={() => scroll(CONST_CARD_SIZE + CONST_GAP_SIZE)}>
              <Icon name="ChevronRight" size="1.25rem" />
            </SIconButton>
          </>
        )}
      </>
    )
  }

  return (
    <SFutureMessageInspiration className={props.className}>
      <Block>
        <SFutureMessageInspirationHeader isMore={props.more}>
          <div>
            <Title variant={Title_h3_default} as={'h3'}>
              {props.more
                ? t('future_message.get_inspired.more')
                : t('future_message.get_inspired.title')}
            </Title>

            {!props.more && (
              <>
                <Spacer size={[1, 2]} />
                <Text css={{ maxWidth: '30rem' }}>
                  {t('future_message.get_inspired.description')}
                </Text>
              </>
            )}
          </div>
          <HStack className={'desktop-only'} space="1rem">
            {renderNavigation(true)}
          </HStack>
        </SFutureMessageInspirationHeader>
      </Block>

      {!list ? (
        <BlockSpinner />
      ) : (
        <HorizontalScroll
          ref={carouselRef}
          scrollerCss={{ gridGap: `${CONST_GAP_SIZE}px` }}
          initialScroll={30}
        >
          {list?.map((item, index) => {
            return (
              <div
                key={`Future_${index}_${item.uuid}`}
                id={`Future_${index}_${item.uuid}`}
                css={{
                  width: `${CONST_CARD_SIZE}px`,
                  height: '100%',
                  minHeight: `${pxToRem(400)}rem`,
                  display: 'flex',
                }}
              >
                <FutureMessageCardByUuid uuid={item.uuid} openInNewTab={true} />
              </div>
            )
          })}
        </HorizontalScroll>
      )}

      <span className="mobile-only">
        <HStack space="1rem" css={{ alignItems: 'flex-start', marginBottom: '2rem' }}>
          {renderNavigation(false)}
        </HStack>
      </span>

      {props?.onAction && (
        <Button
          type="button"
          css={{ margin: '0 auto' }}
          onClick={props.onAction}
          size="large"
          leftIcon={<Icon name="Send" />}
        >
          {t('future_message.get_inspired.cta')}
        </Button>
      )}
    </SFutureMessageInspiration>
  )
}
